import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Download as DownloadIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  GppBad as FailIcon,
  GppGood as OkIcon,
  Group as UsersIcon,
  Lan as SessionIcon,
  Logout as LogoutIcon,
  Public as IpIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { api } from '../../../api/axios';
import { useAppSocket } from '../../../providers/AppSocketContext';
import { useAuth } from '../../../hooks/useAuth';
import { UtilCsv } from '../../../util/UtilCsv';
import DashboardWidget from './DashboardWidget';
import { DashboardInfoTip } from './DashboardInfoTip';
import { canRead, canSignOthersOut } from '../../../util/access';
import { useSessionState } from '../../../hooks/useSessionState';

type Channel = 'app' | 'admin';
type ChannelFilter = 'all' | Channel;
type DualList = { app: { name: string; count: number }[]; admin: { name: string; count: number }[] };

type UsersActivityPayload = {
  viewerIsSuperAdmin?: boolean;
  kpi: {
    totalUsers: number;
    activeAccounts: number;
    inactiveAccounts: number;
    activeSessions: number;
    activeSessionsApp?: number;
    activeSessionsAdmin?: number;
    successfulLogins: number;
    successfulLoginsApp?: number;
    successfulLoginsAdmin?: number;
    failedLogins: number;
    failedLoginsApp?: number;
    failedLoginsAdmin?: number;
    logouts: number;
    tokenSlides: number;
    windowDays: number;
  };
  users: any[];
  activeSessions: any[];
  loginEvents: any[];
  loginsByDay: {
    date: string;
    appSuccess?: number;
    appFailed?: number;
    adminSuccess?: number;
    adminFailed?: number;
  }[];
  loginsByHour: { hour: string; app?: number; admin?: number }[];
  loginsByOs: DualList;
  loginsByBrowser: DualList;
  loginsByDevice: DualList;
  topIps: { name: string; app?: number; admin?: number; count: number }[];
  failedByReason: DualList;
  failedByUser: DualList;
  adminAuditLogs?: any[];
  apiActivityByUser: any[];
  recentApiActivity: any[];
  apiStatusBreakdown: { status: number; count: number }[];
};

const PIE_COLORS = ['#0288d1', '#2e7d32', '#ed6c02', '#7b1fa2', '#d32f2f', '#00838f', '#6d4c41', '#546e7a'];
const APP_COLOR = '#0288d1';
const ADMIN_COLOR = '#7b1fa2';
const APP_FAIL_COLOR = '#ef6c00';
const ADMIN_FAIL_COLOR = '#c62828';

const asDual = (v: DualList | undefined): DualList => ({ app: v?.app || [], admin: v?.admin || [] });

const StatCard = ({
  title,
  value,
  dual,
  color,
  icon,
  loading,
}: {
  title: string;
  value: string | number;
  dual?: { app: number; admin: number; appLabel: string; adminLabel: string };
  color: string;
  icon: React.ReactNode;
  loading?: boolean;
}) => (
  <Card sx={{ height: '100%', borderRadius: 3, borderTop: `3px solid ${color}`, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
    <CardContent sx={{ p: 2.25 }}>
      {loading ? (
        <>
          <Skeleton width="50%" />
          <Skeleton height={36} />
        </>
      ) : (
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
              {value}
            </Typography>
            {dual && (
              <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${dual.appLabel}: ${dual.app}`} sx={{ bgcolor: `${APP_COLOR}18`, color: APP_COLOR, fontWeight: 600 }} />
                <Chip size="small" label={`${dual.adminLabel}: ${dual.admin}`} sx={{ bgcolor: `${ADMIN_COLOR}18`, color: ADMIN_COLOR, fontWeight: 600 }} />
              </Stack>
            )}
          </Box>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}18`, color, display: 'flex' }}>{icon}</Box>
        </Stack>
      )}
    </CardContent>
  </Card>
);

const ChannelToggle = ({
  value,
  onChange,
  labels,
}: {
  value: Channel;
  onChange: (v: Channel) => void;
  labels: { app: string; admin: string };
}) => (
  <ToggleButtonGroup size="small" exclusive value={value} onChange={(_, v) => v && onChange(v)}>
    <ToggleButton value="app" sx={{ textTransform: 'none', px: 1.5 }}>{labels.app}</ToggleButton>
    <ToggleButton value="admin" sx={{ textTransform: 'none', px: 1.5 }}>{labels.admin}</ToggleButton>
  </ToggleButtonGroup>
);

const ChannelChip = ({ channel, labels }: { channel?: string; labels: { app: string; admin: string } }) => {
  const isAdmin = channel === 'admin';
  return (
    <Chip
      size="small"
      label={isAdmin ? labels.admin : labels.app}
      sx={{
        fontWeight: 600,
        bgcolor: isAdmin ? `${ADMIN_COLOR}18` : `${APP_COLOR}18`,
        color: isAdmin ? ADMIN_COLOR : APP_COLOR,
      }}
    />
  );
};

function usePagedRows<T>(rows: T[], resetKey: string) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  useEffect(() => {
    setPage(0);
  }, [resetKey]);
  const paged = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );
  return {
    page,
    rowsPerPage,
    paged,
    setPage,
    setRowsPerPage,
    count: rows.length,
  };
}

const emptyPayload = (): UsersActivityPayload => ({
  viewerIsSuperAdmin: false,
  kpi: {
    totalUsers: 0,
    activeAccounts: 0,
    inactiveAccounts: 0,
    activeSessions: 0,
    activeSessionsApp: 0,
    activeSessionsAdmin: 0,
    successfulLogins: 0,
    successfulLoginsApp: 0,
    successfulLoginsAdmin: 0,
    failedLogins: 0,
    failedLoginsApp: 0,
    failedLoginsAdmin: 0,
    logouts: 0,
    tokenSlides: 0,
    windowDays: 14,
  },
  users: [],
  activeSessions: [],
  loginEvents: [],
  loginsByDay: [],
  loginsByHour: [],
  loginsByOs: { app: [], admin: [] },
  loginsByBrowser: { app: [], admin: [] },
  loginsByDevice: { app: [], admin: [] },
  topIps: [],
  failedByReason: { app: [], admin: [] },
  failedByUser: { app: [], admin: [] },
  adminAuditLogs: [],
  apiActivityByUser: [],
  recentApiActivity: [],
  apiStatusBreakdown: [],
});

const UsersActivity = () => {
  const { t } = useTranslation();
  const { appSettings } = useAppSocket();
  const { session, access } = useAuth();
  const themeColor = appSettings?.UI_THEME_PRIMARY?.value || '#8b1010';
  const [days, setDays] = useSessionState('usersActivity:days', 14);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<UsersActivityPayload>(emptyPayload());
  const [busyJti, setBusyJti] = useState<string | null>(null);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);

  // per-chart App / Admin toggles
  const [osChannel, setOsChannel] = useState<Channel>('app');
  const [browserChannel, setBrowserChannel] = useState<Channel>('app');
  const [deviceChannel, setDeviceChannel] = useState<Channel>('app');
  const [failReasonChannel, setFailReasonChannel] = useState<Channel>('app');
  const [failUserChannel, setFailUserChannel] = useState<Channel>('app');

  // table filters
  const [sessionQ, setSessionQ] = useSessionState('usersActivity:sessionQ', '');
  const [sessionChannel, setSessionChannel] = useSessionState<ChannelFilter>('usersActivity:sessionChannel', 'all');
  const [userQ, setUserQ] = useSessionState('usersActivity:userQ', '');
  const [userStatus, setUserStatus] = useSessionState<'all' | 'active' | 'inactive' | 'online'>('usersActivity:userStatus', 'all');
  const [eventQ, setEventQ] = useSessionState('usersActivity:eventQ', '');
  const [eventChannel, setEventChannel] = useSessionState<ChannelFilter>('usersActivity:eventChannel', 'all');
  const [eventOutcome, setEventOutcome] = useSessionState<'all' | 'success' | 'failed'>('usersActivity:eventOutcome', 'all');
  const [apiQ, setApiQ] = useSessionState('usersActivity:apiQ', '');
  const [auditQ, setAuditQ] = useSessionState('usersActivity:auditQ', '');

  const canManageSessions =
    canSignOthersOut(access)
    || canRead(access, 'admin_portal')
    || Number(session?.user_id) === 1;

  const channelLabels = {
    app: t('common:dashboard.usersActivity.channel.app'),
    admin: t('common:dashboard.usersActivity.channel.admin'),
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/dashboard/sections/users', { params: { days } });
      if (res?.status === 200 && res.data) {
        setData({ ...emptyPayload(), ...res.data, kpi: { ...emptyPayload().kpi, ...res.data.kpi } });
      } else {
        setError(t('common:dashboard.usersActivity.loadError'));
      }
    } catch {
      setError(t('common:dashboard.usersActivity.loadError'));
    } finally {
      setLoading(false);
    }
  }, [days, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredSessions = useMemo(() => {
    const q = sessionQ.trim().toLowerCase();
    return data.activeSessions.filter((s) => {
      if (sessionChannel !== 'all' && (s.channel || 'app') !== sessionChannel) return false;
      if (!q) return true;
      return [s.username, s.clientIp, s.os, s.browser, s.hostname, s.platform, s.device, s.channel]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [data.activeSessions, sessionQ, sessionChannel]);

  const filteredUsers = useMemo(() => {
    const q = userQ.trim().toLowerCase();
    return data.users.filter((u) => {
      if (userStatus === 'active' && !u.active) return false;
      if (userStatus === 'inactive' && u.active) return false;
      if (userStatus === 'online' && !u.hasActiveSession) return false;
      if (!q) return true;
      return (
        String(u.username || '').toLowerCase().includes(q)
        || (u.roles || []).some((r: string) => String(r).toLowerCase().includes(q))
        || String(u.id).includes(q)
      );
    });
  }, [data.users, userQ, userStatus]);

  const filteredEvents = useMemo(() => {
    const q = eventQ.trim().toLowerCase();
    return data.loginEvents.filter((e) => {
      if (eventChannel !== 'all' && (e.channel || 'app') !== eventChannel) return false;
      if (eventOutcome === 'success' && !e.success) return false;
      if (eventOutcome === 'failed' && e.success) return false;
      if (!q) return true;
      return [e.username, e.eventType, e.clientIp, e.failureReason, e.os, e.browser, e.channel]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [data.loginEvents, eventQ, eventChannel, eventOutcome]);

  const filteredApi = useMemo(() => {
    const q = apiQ.trim().toLowerCase();
    if (!q) return data.recentApiActivity;
    return data.recentApiActivity.filter((r) =>
      [r.username, r.method, r.path, r.status, r.clientIp]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [data.recentApiActivity, apiQ]);

  const filteredAudit = useMemo(() => {
    const q = auditQ.trim().toLowerCase();
    if (!q) return data.adminAuditLogs || [];
    return (data.adminAuditLogs || []).filter((r) =>
      [r.username, r.method, r.path, r.status, r.clientIp]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [data.adminAuditLogs, auditQ]);

  const sessionsPage = usePagedRows(filteredSessions, `${sessionQ}|${sessionChannel}|${days}`);
  const usersPage = usePagedRows(filteredUsers, `${userQ}|${userStatus}|${days}`);
  const eventsPage = usePagedRows(filteredEvents, `${eventQ}|${eventChannel}|${eventOutcome}|${days}`);
  const apiPage = usePagedRows(filteredApi, `${apiQ}|${days}`);
  const auditPage = usePagedRows(filteredAudit, `${auditQ}|${days}`);

  const exportCsv = (rows: Record<string, unknown>[], filename: string) => {
    if (!rows.length) {
      toast.info(t('common:dashboard.noData'));
      return;
    }
    UtilCsv.handleExportData(rows, filename);
  };

  const exportSessions = () =>
    exportCsv(
      filteredSessions.map((s) => ({
        channel: s.channel,
        userId: s.userId,
        username: s.username,
        jti: s.jti,
        ip: s.clientIp,
        os: s.os,
        browser: s.browser,
        device: s.device,
        lastSeen: s.lastSeen,
        exp: s.exp,
      })),
      'active_sessions'
    );

  const exportUsers = () =>
    exportCsv(
      filteredUsers.map((u) => ({
        id: u.id,
        username: u.username,
        active: u.active,
        roles: (u.roles || []).join('|'),
        appSessions: u.appSessionCount ?? 0,
        adminSessions: u.adminSessionCount ?? 0,
        lastAppSeen: u.appSession?.lastSeen,
        lastAdminSeen: u.adminSession?.lastSeen,
      })),
      'users'
    );

  const exportLoginEvents = () =>
    exportCsv(
      filteredEvents.map((e) => ({
        created: e.created,
        channel: e.channel,
        username: e.username,
        success: e.success,
        eventType: e.eventType,
        failureReason: e.failureReason,
        ip: e.clientIp,
        os: e.os,
        browser: e.browser,
      })),
      'login_events'
    );

  const exportApiActivity = () =>
    exportCsv(
      data.recentApiActivity.map((r) => ({
        created: r.created,
        username: r.username,
        method: r.method,
        path: r.path,
        status: r.status,
        durationMs: r.durationMs,
        ip: r.clientIp,
      })),
      'api_activity'
    );

  const revokeSession = async (jti: string) => {
    if (!jti || !canManageSessions) return;
    if (!window.confirm(t('common:dashboard.usersActivity.confirmRevokeSession'))) return;
    setBusyJti(jti);
    try {
      const res = await api.post(`/dashboard/sections/users/sessions/${encodeURIComponent(jti)}/revoke`);
      if (res?.status === 200) {
        toast.success(t('common:dashboard.usersActivity.revokeSessionOk'));
        await load();
      } else {
        toast.error(t('common:dashboard.usersActivity.revokeFailed'));
      }
    } catch {
      toast.error(t('common:dashboard.usersActivity.revokeFailed'));
    } finally {
      setBusyJti(null);
    }
  };

  const revokeUserSessions = async (userId: number, channel: ChannelFilter = 'all') => {
    if (!userId || !canManageSessions) return;
    const msg =
      channel === 'app'
        ? t('common:dashboard.usersActivity.confirmRevokeUserApp')
        : channel === 'admin'
          ? t('common:dashboard.usersActivity.confirmRevokeUserAdmin')
          : t('common:dashboard.usersActivity.confirmRevokeUser');
    if (!window.confirm(msg)) return;
    setBusyUserId(userId);
    try {
      const isSelf = session?.user_id === userId;
      const res = await api.post(
        `/dashboard/sections/users/${userId}/sessions/revoke`,
        null,
        {
          params: {
            channel,
            ...(isSelf && channel !== 'admin' ? { include_self: false } : {}),
          },
        }
      );
      if (res?.status === 200) {
        toast.success(
          t('common:dashboard.usersActivity.revokeUserOk', {
            count: res.data?.revoked ?? 0,
          })
        );
        await load();
      } else {
        toast.error(t('common:dashboard.usersActivity.revokeFailed'));
      }
    } catch {
      toast.error(t('common:dashboard.usersActivity.revokeFailed'));
    } finally {
      setBusyUserId(null);
    }
  };

  const k = data.kpi;
  const isSuperAdminViewer =
    Boolean(data.viewerIsSuperAdmin)
    || Number(session?.user_id) === 1;

  const dualOs = asDual(data.loginsByOs);
  const dualBrowser = asDual(data.loginsByBrowser);
  const dualDevice = asDual(data.loginsByDevice);
  const dualFailReason = asDual(data.failedByReason);
  const dualFailUser = asDual(data.failedByUser);

  const pieBlocks: {
    key: string;
    title: string;
    help: string;
    data: { name: string; count: number }[];
    channel: Channel;
    setChannel: (v: Channel) => void;
  }[] = [
    {
      key: 'os',
      title: t('common:dashboard.usersActivity.charts.byOs'),
      help: 'usersActivity.byOs',
      data: dualOs[osChannel],
      channel: osChannel,
      setChannel: setOsChannel,
    },
    {
      key: 'browser',
      title: t('common:dashboard.usersActivity.charts.byBrowser'),
      help: 'usersActivity.byBrowser',
      data: dualBrowser[browserChannel],
      channel: browserChannel,
      setChannel: setBrowserChannel,
    },
    {
      key: 'device',
      title: t('common:dashboard.usersActivity.charts.byDevice'),
      help: 'usersActivity.byDevice',
      data: dualDevice[deviceChannel],
      channel: deviceChannel,
      setChannel: setDeviceChannel,
    },
  ];

  const tablePager = (
    pageState: ReturnType<typeof usePagedRows>,
  ) => (
    <TablePagination
      component="div"
      count={pageState.count}
      page={pageState.page}
      onPageChange={(_, p) => pageState.setPage(p)}
      rowsPerPage={pageState.rowsPerPage}
      onRowsPerPageChange={(e) => {
        pageState.setRowsPerPage(parseInt(e.target.value, 10));
        pageState.setPage(0);
      }}
      rowsPerPageOptions={[5, 10, 25, 50]}
      labelRowsPerPage={t('common:dashboard.usersActivity.rowsPerPage')}
    />
  );

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon sx={{ color: themeColor }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('common:dashboard.usersActivity.title')}
          </Typography>
          <DashboardInfoTip helpKey="usersActivity.overview" />
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
            <ToggleButton value={7}>7d</ToggleButton>
            <ToggleButton value={14}>14d</ToggleButton>
            <ToggleButton value={30}>30d</ToggleButton>
            <ToggleButton value={90}>90d</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportSessions}>
            {t('common:dashboard.usersActivity.exportSessions')}
          </Button>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportUsers}>
            {t('common:dashboard.usersActivity.exportUsers')}
          </Button>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportLoginEvents}>
            {t('common:dashboard.usersActivity.exportEvents')}
          </Button>
          {isSuperAdminViewer && (
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportApiActivity}>
              {t('common:dashboard.usersActivity.exportApi')}
            </Button>
          )}
          <IconButton onClick={() => void load()} disabled={loading} aria-label={t('common:dashboard.refresh')}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: themeColor } }} />}
      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        {[
          {
            title: t('common:dashboard.usersActivity.kpi.totalUsers'),
            value: k.totalUsers,
            color: '#1565c0',
            icon: <UsersIcon />,
            dual: undefined as undefined | { app: number; admin: number; appLabel: string; adminLabel: string },
          },
          {
            title: t('common:dashboard.usersActivity.kpi.activeSessions'),
            value: k.activeSessions,
            color: '#2e7d32',
            icon: <SessionIcon />,
            dual: {
              app: k.activeSessionsApp ?? 0,
              admin: k.activeSessionsAdmin ?? 0,
              appLabel: channelLabels.app,
              adminLabel: channelLabels.admin,
            },
          },
          {
            title: t('common:dashboard.usersActivity.kpi.successfulLogins'),
            value: k.successfulLogins,
            color: '#00838f',
            icon: <OkIcon />,
            dual: {
              app: k.successfulLoginsApp ?? 0,
              admin: k.successfulLoginsAdmin ?? 0,
              appLabel: channelLabels.app,
              adminLabel: channelLabels.admin,
            },
          },
          {
            title: t('common:dashboard.usersActivity.kpi.failedLogins'),
            value: k.failedLogins,
            color: '#c62828',
            icon: <FailIcon />,
            dual: {
              app: k.failedLoginsApp ?? 0,
              admin: k.failedLoginsAdmin ?? 0,
              appLabel: channelLabels.app,
              adminLabel: channelLabels.admin,
            },
          },
        ].map((card) => (
          <Grid key={card.title} size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title={card.title}
              value={card.value}
              dual={card.dual}
              color={card.color}
              icon={card.icon}
              loading={loading && !data.users.length}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <DashboardWidget
            title={t('common:dashboard.usersActivity.charts.loginsOverTime')}
            helpKey="usersActivity.loginsOverTime"
            chartHeight={300}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.loginsByDay}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Legend />
                <Line type="monotone" dataKey="appSuccess" name={`${channelLabels.app} ✓`} stroke={APP_COLOR} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="appFailed" name={`${channelLabels.app} ✗`} stroke={APP_FAIL_COLOR} strokeWidth={2} dot={false} strokeDasharray="4 3" />
                <Line type="monotone" dataKey="adminSuccess" name={`${channelLabels.admin} ✓`} stroke={ADMIN_COLOR} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="adminFailed" name={`${channelLabels.admin} ✗`} stroke={ADMIN_FAIL_COLOR} strokeWidth={2} dot={false} strokeDasharray="4 3" />
              </LineChart>
            </ResponsiveContainer>
          </DashboardWidget>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <DashboardWidget
            title={t('common:dashboard.usersActivity.charts.loginsByHour')}
            helpKey="usersActivity.loginsByHour"
            chartHeight={300}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.loginsByHour}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="app" name={channelLabels.app} stackId="a" fill={APP_COLOR} radius={[0, 0, 0, 0]} />
                <Bar dataKey="admin" name={channelLabels.admin} stackId="a" fill={ADMIN_COLOR} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </DashboardWidget>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {pieBlocks.map((block) => (
          <Grid key={block.key} size={{ xs: 12, md: 4 }}>
            <DashboardWidget
              title={`${block.title} · ${channelLabels[block.channel]}`}
              helpKey={block.help}
              chartHeight={260}
              headerActions={
                <ChannelToggle value={block.channel} onChange={block.setChannel} labels={channelLabels} />
              }
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={block.data} dataKey="count" nameKey="name" outerRadius={90} label>
                    {block.data.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </DashboardWidget>
          </Grid>
        ))}
      </Grid>

      {/* Active sessions */}
      <DashboardWidget
        title={t('common:dashboard.usersActivity.tables.activeSessions')}
        helpKey="usersActivity.activeSessions"
        chartHeight={420}
        scrollable
        headerActions={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              value={sessionQ}
              onChange={(e) => setSessionQ(e.target.value)}
              placeholder={t('common:dashboard.usersActivity.search')}
              sx={{ minWidth: 180 }}
            />
            <TextField
              select
              size="small"
              value={sessionChannel}
              onChange={(e) => setSessionChannel(e.target.value as ChannelFilter)}
              sx={{ minWidth: 120 }}
              label={t('common:dashboard.usersActivity.cols.channel')}
            >
              <MenuItem value="all">{t('common:dashboard.usersActivity.filterAll')}</MenuItem>
              <MenuItem value="app">{channelLabels.app}</MenuItem>
              <MenuItem value="admin">{channelLabels.admin}</MenuItem>
            </TextField>
          </Stack>
        }
      >
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('common:dashboard.usersActivity.cols.channel')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.user')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.ip')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.os')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.browser')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.host')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.lastSeen')}</TableCell>
                {canManageSessions && <TableCell align="right">{t('common:dashboard.usersActivity.cols.actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sessionsPage.paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManageSessions ? 8 : 7}>
                    <Typography variant="body2" color="text.secondary">{t('common:dashboard.noData')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sessionsPage.paged.map((s) => (
                  <TableRow key={s.jti || `${s.userId}-${s.lastSeen}`}>
                    <TableCell><ChannelChip channel={s.channel} labels={channelLabels} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{s.username || s.userId}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.device || '—'}</Typography>
                    </TableCell>
                    <TableCell>{s.clientIp || '—'}</TableCell>
                    <TableCell>{s.os || '—'}</TableCell>
                    <TableCell>{s.browser || '—'}</TableCell>
                    <TableCell>{s.hostname || s.platform || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{s.lastSeen ? new Date(s.lastSeen).toLocaleString() : '—'}</Typography>
                    </TableCell>
                    {canManageSessions && (
                      <TableCell align="right">
                        <Tooltip title={t('common:dashboard.usersActivity.forceLogoutSession')}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!s.jti || busyJti === s.jti}
                              onClick={() => void revokeSession(s.jti)}
                            >
                              <LogoutIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {tablePager(sessionsPage)}
      </DashboardWidget>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <DashboardWidget
            title={t('common:dashboard.usersActivity.tables.topIps')}
            helpKey="usersActivity.topIps"
            chartHeight={320}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.topIps} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="app" name={channelLabels.app} stackId="ip" fill={APP_COLOR} />
                <Bar dataKey="admin" name={channelLabels.admin} stackId="ip" fill={ADMIN_COLOR} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </DashboardWidget>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Stack spacing={2}>
            <DashboardWidget
              title={`${t('common:dashboard.usersActivity.charts.failedByReason')} · ${channelLabels[failReasonChannel]}`}
              helpKey="usersActivity.failedByReason"
              chartHeight={170}
              headerActions={
                <ChannelToggle value={failReasonChannel} onChange={setFailReasonChannel} labels={channelLabels} />
              }
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dualFailReason[failReasonChannel]}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill={failReasonChannel === 'admin' ? ADMIN_FAIL_COLOR : APP_FAIL_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </DashboardWidget>
            <DashboardWidget
              title={`${t('common:dashboard.usersActivity.charts.failedByUser')} · ${channelLabels[failUserChannel]}`}
              helpKey="usersActivity.failedByUser"
              chartHeight={170}
              headerActions={
                <ChannelToggle value={failUserChannel} onChange={setFailUserChannel} labels={channelLabels} />
              }
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dualFailUser[failUserChannel]}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill={failUserChannel === 'admin' ? ADMIN_COLOR : '#ef6c00'} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </DashboardWidget>
          </Stack>
        </Grid>
      </Grid>

      {/* All users */}
      <DashboardWidget
        title={t('common:dashboard.usersActivity.tables.users')}
        helpKey="usersActivity.users"
        chartHeight={460}
        scrollable
        headerActions={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              value={userQ}
              onChange={(e) => setUserQ(e.target.value)}
              placeholder={t('common:dashboard.usersActivity.filterUsers')}
              sx={{ minWidth: 180 }}
            />
            <TextField
              select
              size="small"
              value={userStatus}
              onChange={(e) => setUserStatus(e.target.value as typeof userStatus)}
              sx={{ minWidth: 130 }}
              label={t('common:dashboard.usersActivity.cols.status')}
            >
              <MenuItem value="all">{t('common:dashboard.usersActivity.filterAll')}</MenuItem>
              <MenuItem value="active">{t('common:dashboard.usersActivity.active')}</MenuItem>
              <MenuItem value="inactive">{t('common:dashboard.usersActivity.inactive')}</MenuItem>
              <MenuItem value="online">{t('common:dashboard.usersActivity.online')}</MenuItem>
            </TextField>
          </Stack>
        }
      >
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>{t('common:dashboard.usersActivity.cols.user')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.status')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.roles')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.appSessions')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.adminSessions')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.lastSeen')}</TableCell>
                {canManageSessions && <TableCell align="right">{t('common:dashboard.usersActivity.cols.actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {usersPage.paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManageSessions ? 8 : 7}>
                    <Typography variant="body2" color="text.secondary">{t('common:dashboard.noData')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                usersPage.paged.map((u) => {
                  const sessions: any[] = Array.isArray(u.sessions) ? u.sessions : [];
                  const expanded = expandedUserId === u.id;
                  const colSpan = canManageSessions ? 8 : 7;
                  return (
                    <Fragment key={u.id}>
                      <TableRow hover>
                        <TableCell padding="checkbox">
                          <Tooltip title={t('common:dashboard.usersActivity.toggleSessions')}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={!u.hasActiveSession}
                                onClick={() => setExpandedUserId(expanded ? null : u.id)}
                                aria-label={t('common:dashboard.usersActivity.toggleSessions')}
                              >
                                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{u.username}</Typography>
                          <Typography variant="caption" color="text.secondary">#{u.id} · {u.lang || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={u.active ? t('common:dashboard.usersActivity.active') : t('common:dashboard.usersActivity.inactive')}
                            color={u.active ? 'success' : 'default'}
                            variant={u.active ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" gap={0.5} flexWrap="wrap">
                            {(u.roles || []).length
                              ? u.roles.map((r: string) => <Chip key={r} size="small" label={r} variant="outlined" />)
                              : '—'}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {(u.appSessionCount ?? 0) > 0 ? (
                            <Chip
                              size="small"
                              color="info"
                              icon={<SessionIcon />}
                              label={t('common:dashboard.usersActivity.sessionsCount', { count: u.appSessionCount })}
                            />
                          ) : (
                            <Chip size="small" variant="outlined" label={t('common:dashboard.usersActivity.offline')} />
                          )}
                        </TableCell>
                        <TableCell>
                          {(u.adminSessionCount ?? 0) > 0 ? (
                            <Chip
                              size="small"
                              sx={{ bgcolor: `${ADMIN_COLOR}18`, color: ADMIN_COLOR }}
                              icon={<SecurityIcon />}
                              label={t('common:dashboard.usersActivity.sessionsCount', { count: u.adminSessionCount })}
                            />
                          ) : (
                            <Chip size="small" variant="outlined" label={t('common:dashboard.usersActivity.offline')} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {channelLabels.app}: {u.appSession?.lastSeen ? new Date(u.appSession.lastSeen).toLocaleString() : '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {channelLabels.admin}: {u.adminSession?.lastSeen ? new Date(u.adminSession.lastSeen).toLocaleString() : '—'}
                          </Typography>
                        </TableCell>
                        {canManageSessions && (
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                              <Tooltip title={t('common:dashboard.usersActivity.forceLogoutApp')}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="info"
                                    disabled={!(u.appSessionCount > 0) || busyUserId === u.id}
                                    onClick={() => void revokeUserSessions(u.id, 'app')}
                                  >
                                    <LogoutIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={t('common:dashboard.usersActivity.forceLogoutAdmin')}>
                                <span>
                                  <IconButton
                                    size="small"
                                    sx={{ color: ADMIN_COLOR }}
                                    disabled={!(u.adminSessionCount > 0) || busyUserId === u.id}
                                    onClick={() => void revokeUserSessions(u.id, 'admin')}
                                  >
                                    <SecurityIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={t('common:dashboard.usersActivity.forceLogoutUser')}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={!u.hasActiveSession || busyUserId === u.id}
                                    onClick={() => void revokeUserSessions(u.id, 'all')}
                                  >
                                    <LogoutIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, px: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                {t('common:dashboard.usersActivity.perSessionHint')}
                              </Typography>
                              {sessions.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">{t('common:dashboard.noData')}</Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>{t('common:dashboard.usersActivity.cols.channel')}</TableCell>
                                      <TableCell>{t('common:dashboard.usersActivity.cols.ip')}</TableCell>
                                      <TableCell>{t('common:dashboard.usersActivity.cols.client')}</TableCell>
                                      <TableCell>{t('common:dashboard.usersActivity.cols.lastSeen')}</TableCell>
                                      {canManageSessions && (
                                        <TableCell align="right">{t('common:dashboard.usersActivity.cols.actions')}</TableCell>
                                      )}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {sessions.map((s: any) => (
                                      <TableRow key={s.jti || `${s.channel}-${s.lastSeen}`}>
                                        <TableCell><ChannelChip channel={s.channel} labels={channelLabels} /></TableCell>
                                        <TableCell>{s.clientIp || '—'}</TableCell>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {[s.os, s.browser, s.device].filter(Boolean).join(' · ') || '—'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {s.lastSeen ? new Date(s.lastSeen).toLocaleString() : '—'}
                                          </Typography>
                                        </TableCell>
                                        {canManageSessions && (
                                          <TableCell align="right">
                                            <Tooltip title={t('common:dashboard.usersActivity.forceLogoutSession')}>
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  disabled={!s.jti || busyJti === s.jti}
                                                  onClick={() => void revokeSession(s.jti)}
                                                >
                                                  <LogoutIcon fontSize="small" />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                          </TableCell>
                                        )}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {tablePager(usersPage)}
      </DashboardWidget>

      {/* Merged auth events (app + admin panel) */}
      <DashboardWidget
        title={t('common:dashboard.usersActivity.tables.loginEvents')}
        helpKey="usersActivity.loginEvents"
        chartHeight={440}
        scrollable
        headerActions={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              value={eventQ}
              onChange={(e) => setEventQ(e.target.value)}
              placeholder={t('common:dashboard.usersActivity.search')}
              sx={{ minWidth: 160 }}
            />
            <TextField
              select
              size="small"
              value={eventChannel}
              onChange={(e) => setEventChannel(e.target.value as ChannelFilter)}
              sx={{ minWidth: 120 }}
              label={t('common:dashboard.usersActivity.cols.channel')}
            >
              <MenuItem value="all">{t('common:dashboard.usersActivity.filterAll')}</MenuItem>
              <MenuItem value="app">{channelLabels.app}</MenuItem>
              <MenuItem value="admin">{channelLabels.admin}</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              value={eventOutcome}
              onChange={(e) => setEventOutcome(e.target.value as typeof eventOutcome)}
              sx={{ minWidth: 120 }}
              label={t('common:dashboard.usersActivity.cols.event')}
            >
              <MenuItem value="all">{t('common:dashboard.usersActivity.filterAll')}</MenuItem>
              <MenuItem value="success">{t('common:dashboard.usersActivity.success')}</MenuItem>
              <MenuItem value="failed">{t('common:dashboard.usersActivity.failed')}</MenuItem>
            </TextField>
          </Stack>
        }
      >
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('common:dashboard.usersActivity.cols.when')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.channel')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.user')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.event')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.ip')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.client')}</TableCell>
                <TableCell>{t('common:dashboard.usersActivity.cols.detail')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {eventsPage.paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">{t('common:dashboard.noData')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                eventsPage.paged.map((e, idx) => (
                  <TableRow key={e.id || `${e.created}-${idx}`}>
                    <TableCell>
                      <Typography variant="caption">
                        {e.created ? new Date(e.created).toLocaleString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell><ChannelChip channel={e.channel} labels={channelLabels} /></TableCell>
                    <TableCell>{e.username}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={e.success ? (String(e.eventType || '').includes('logout') ? 'default' : 'success') : 'error'}
                        label={e.eventType || (e.success ? 'login' : 'fail')}
                      />
                    </TableCell>
                    <TableCell>{e.clientIp || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {[e.os, e.browser, e.device].filter(Boolean).join(' · ') || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {e.failureReason || e.hostname || e.timezone || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {tablePager(eventsPage)}
      </DashboardWidget>

      {isSuperAdminViewer && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 5 }}>
            <DashboardWidget
              title={t('common:dashboard.usersActivity.tables.apiByUser')}
              helpKey="usersActivity.apiByUser"
              chartHeight={340}
              scrollable
            >
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common:dashboard.usersActivity.cols.user')}</TableCell>
                      <TableCell align="right">{t('common:dashboard.usersActivity.cols.requests')}</TableCell>
                      <TableCell align="right">{t('common:dashboard.usersActivity.cols.errors')}</TableCell>
                      <TableCell align="right">{t('common:dashboard.usersActivity.cols.avgMs')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.apiActivityByUser.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">{t('common:dashboard.noData')}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.apiActivityByUser.map((r) => (
                        <TableRow key={`${r.userId}-${r.username}`}>
                          <TableCell>{r.username}</TableCell>
                          <TableCell align="right">{r.requests}</TableCell>
                          <TableCell align="right">{r.errors}</TableCell>
                          <TableCell align="right">{r.avgDurationMs}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DashboardWidget>
          </Grid>
          <Grid size={{ xs: 12, lg: 7 }}>
            <DashboardWidget
              title={t('common:dashboard.usersActivity.tables.recentApi')}
              helpKey="usersActivity.recentApi"
              chartHeight={340}
              scrollable
              headerActions={
                <TextField
                  size="small"
                  value={apiQ}
                  onChange={(e) => setApiQ(e.target.value)}
                  placeholder={t('common:dashboard.usersActivity.search')}
                  sx={{ minWidth: 180 }}
                />
              }
            >
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common:dashboard.usersActivity.cols.when')}</TableCell>
                      <TableCell>{t('common:dashboard.usersActivity.cols.user')}</TableCell>
                      <TableCell>{t('common:dashboard.usersActivity.cols.method')}</TableCell>
                      <TableCell>{t('common:dashboard.usersActivity.cols.path')}</TableCell>
                      <TableCell>{t('common:dashboard.usersActivity.cols.status')}</TableCell>
                      <TableCell>{t('common:dashboard.usersActivity.cols.ip')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiPage.paged.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <Typography variant="caption">
                            {r.created ? new Date(r.created).toLocaleString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{r.username}</TableCell>
                        <TableCell><Chip size="small" label={r.method} variant="outlined" /></TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{r.path}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.status}
                            color={r.status >= 400 ? 'error' : r.status >= 300 ? 'warning' : 'success'}
                          />
                        </TableCell>
                        <TableCell>{r.clientIp || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {tablePager(apiPage)}
            </DashboardWidget>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <DashboardWidget
              title={t('common:dashboard.usersActivity.tables.adminAudit')}
              helpKey="usersActivity.adminAudit"
              chartHeight={320}
              scrollable
              headerActions={
                <TextField
                  size="small"
                  value={auditQ}
                  onChange={(e) => setAuditQ(e.target.value)}
                  placeholder={t('common:dashboard.usersActivity.search')}
                  sx={{ minWidth: 180 }}
                />
              }
            >
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common:dashboard.usersActivity.cols.when')}</TableCell>
                      <TableCell>{t('common:dashboard.usersActivity.cols.user')}</TableCell>
                      <TableCell>{t('common:dashboard.usersActivity.cols.method')}</TableCell>
                      <TableCell>{t('common:dashboard.usersActivity.cols.path')}</TableCell>
                      <TableCell>{t('common:dashboard.usersActivity.cols.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditPage.paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">{t('common:dashboard.noData')}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      auditPage.paged.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>
                            <Typography variant="caption">
                              {r.created ? new Date(r.created).toLocaleString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{r.username}</TableCell>
                          <TableCell><Chip size="small" label={r.method} variant="outlined" /></TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{r.path}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={r.status} color={r.status >= 400 ? 'error' : 'success'} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {tablePager(auditPage)}
            </DashboardWidget>
          </Grid>
        </Grid>
      )}

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <IpIcon fontSize="small" color="action" />
        <Typography variant="caption" color="text.secondary">
          {t(
            isSuperAdminViewer
              ? 'common:dashboard.usersActivity.footnoteSuperAdmin'
              : 'common:dashboard.usersActivity.footnoteOperator'
          )}
        </Typography>
      </Paper>
    </Stack>
  );
};

export default UsersActivity;
